// src/pages/dashboard/PublicProfile.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  User, Camera, Sparkles, Globe, Mail, Share2
} from 'lucide-react';
import { useToast } from '../../components/ToastNotification';
import ModalitySelector from '../../components/common/ModalitySelector';
import SocialMediaConnections from '../../components/SocialMediaConnections';
import WebsiteUrlInput from '../../components/WebsiteUrlInput';
import BioGeneratorModal from '../../components/BioGeneratorModal';
import AboutGeneratorModal from '../../components/AboutGeneratorModal';
import { formatPhoneNumber } from '../../utils/phoneFormatter';
import firestoreService from '../../services/firestore-service';
import PhotoUpload from '../../components/common/PhotoUpload';
import { useScrollToHash } from '../../utils/useScrollToHash';
import { isOldModalityFormat, migrateModalities } from '../../utils/migrateModalities';

/** Parse modalities from any stored format into the structured shape. */
const parseModalities = (raw) => {
  if (raw && typeof raw === 'object' && !Array.isArray(raw) && raw.selected !== undefined) {
    return {
      selected: Array.isArray(raw.selected) ? raw.selected : [],
      custom: Array.isArray(raw.custom) ? raw.custom : [],
      parentCategories: Array.isArray(raw.parentCategories) ? raw.parentCategories : [],
    };
  }
  // Old flat array (pre-taxonomy checkbox grid) — migrate instead of discarding
  if (isOldModalityFormat(raw)) {
    return migrateModalities(raw);
  }
  return { selected: [], custom: [], parentCategories: [] };
};

const PublicProfile = () => {
  useScrollToHash();
  const toast = useToast();
  const [displayName, setDisplayName] = useState('');
  const [modalities, setModalities] = useState({ selected: [], custom: [], parentCategories: [] });
  const [bio, setBio] = useState('');
  const [about, setAbout] = useState('');
  const [businessEmail, setBusinessEmail] = useState('');
  const [showEmailPublicly, setShowEmailPublicly] = useState(false);
  const [businessPhone, setBusinessPhone] = useState('');
  const [showPhonePublicly, setShowPhonePublicly] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profilePhotoPath, setProfilePhotoPath] = useState(null);
  const [emailError, setEmailError] = useState('');

  const [socialConnections, setSocialConnections] = useState({
    instagram: { connected: false, username: null, followers: null },
    facebook: { connected: false, pageName: null, followers: null },
    tiktok: { connected: false, username: null, followers: null }
  });
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [websiteVerified, setWebsiteVerified] = useState(null); // null, 'checking', 'verified', 'failed'

  const [showBioModal, setShowBioModal] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved'

  // Load existing profile data on mount
  useEffect(() => {
    // Load from localStorage first (fast)
    const savedData = JSON.parse(localStorage.getItem('practitioner_setup') || '{}');
    const profile = savedData.profile || {};

    if (profile.displayName) setDisplayName(profile.displayName);
    if (profile.modalities) setModalities(parseModalities(profile.modalities));
    if (profile.bio) setBio(profile.bio);
    if (profile.about) setAbout(profile.about);
    if (profile.businessEmail) setBusinessEmail(profile.businessEmail);
    if (profile.showEmail !== undefined) setShowEmailPublicly(profile.showEmail);
    if (profile.businessPhone) setBusinessPhone(profile.businessPhone);
    if (profile.showPhone !== undefined) setShowPhonePublicly(profile.showPhone);
    if (profile.photo) setProfilePhoto(profile.photo);
    if (profile.photoPath) setProfilePhotoPath(profile.photoPath);
    if (profile.socialConnections) setSocialConnections(profile.socialConnections);
    if (profile.websiteUrl) setWebsiteUrl(profile.websiteUrl);

    // Background: sync from Firestore if localStorage is empty
    if (!savedData || !savedData.profile || Object.keys(savedData.profile).length === 0) {
      const userId = firestoreService.getCurrentUserId();
      if (userId) {
        firestoreService.getProfile(userId).then(firestoreData => {
          if (firestoreData && Object.keys(firestoreData).length > 0) {
            // Cache to localStorage for next time
            const updatedData = { ...savedData, profile: firestoreData };
            localStorage.setItem('practitioner_setup', JSON.stringify(updatedData));

            // Update component state
            if (firestoreData.displayName) setDisplayName(firestoreData.displayName);
            if (firestoreData.modalities) setModalities(parseModalities(firestoreData.modalities));
            if (firestoreData.bio) setBio(firestoreData.bio);
            if (firestoreData.about) setAbout(firestoreData.about);
            if (firestoreData.businessEmail) setBusinessEmail(firestoreData.businessEmail);
            if (firestoreData.showEmail !== undefined) setShowEmailPublicly(firestoreData.showEmail);
            if (firestoreData.businessPhone) setBusinessPhone(firestoreData.businessPhone);
            if (firestoreData.showPhone !== undefined) setShowPhonePublicly(firestoreData.showPhone);
            if (firestoreData.photo) setProfilePhoto(firestoreData.photo);
            if (firestoreData.photoPath) setProfilePhotoPath(firestoreData.photoPath);
            if (firestoreData.socialConnections) setSocialConnections(firestoreData.socialConnections);
            if (firestoreData.websiteUrl) setWebsiteUrl(firestoreData.websiteUrl);
          }
        }).catch(err => console.warn('Firestore profile read failed:', err));
      }
    }
  }, []);

  const bioWordCount = bio.split(' ').filter(w => w).length;
  const aboutWordCount = about.split(' ').filter(w => w).length;

  // Email validation
  const validateEmail = (email) => {
    if (!email) return true; // Empty is ok
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (value) => {
    setBusinessEmail(value);
    if (value && !validateEmail(value)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  const handlePhoneBlur = () => {
    if (businessPhone) {
      setBusinessPhone(formatPhoneNumber(businessPhone));
    }
  };

  const handleVerifyWebsite = async () => {
    if (!websiteUrl) {
      setWebsiteVerified(null);
      return;
    }

    // Validate URL format
    try {
      const urlObj = new URL(websiteUrl);
      if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
        setWebsiteVerified('failed');
        return;
      }
    } catch {
      setWebsiteVerified('failed');
      return;
    }

    // Check if site exists
    setWebsiteVerified('checking');

    try {
      // TODO: Replace with actual backend endpoint
      // For now, simulate check
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Simulate: Check if URL is reachable
      setWebsiteVerified('verified');
    } catch (error) {
      setWebsiteVerified('failed');
    }
  };

  const handleBioGenerated = (generatedBio) => {
    setBio(generatedBio);
  };

  const handleAboutGenerated = (generatedAbout) => {
    setAbout(generatedAbout);
  };

  const handleSaveProfile = () => {
    setSaveStatus('saving');
    // Get existing wizard data
    const existingData = JSON.parse(localStorage.getItem('practitioner_setup') || '{}');

    // Update profile data
    // Auto-compute parentCategories from current selections
    const savedModalities = {
      ...modalities,
      parentCategories: [...new Set(modalities.selected.map((m) => m.parentId))],
    };

    const profileData = {
      ...existingData.profile,
      displayName,
      modalities: savedModalities,
      bio,
      about,
      businessEmail,
      showEmail: showEmailPublicly,
      businessPhone,
      showPhone: showPhonePublicly,
      socialConnections,
      websiteUrl,
      // Only persist Storage URLs — never write Base64 to Firestore
      ...(profilePhoto && !profilePhoto.startsWith('data:') && { photo: profilePhoto }),
      ...(profilePhotoPath && { photoPath: profilePhotoPath })
    };

    const updatedData = {
      ...existingData,
      profile: profileData
    };

    // Save to localStorage (instant)
    localStorage.setItem('practitioner_setup', JSON.stringify(updatedData));

    // Background sync to Firestore
    const userId = firestoreService.getCurrentUserId();
    if (userId) {
      firestoreService.saveProfile(userId, profileData).then(() => {
        console.log('✅ Profile saved to Firestore');
      }).catch(err => {
        console.warn('⚠️ Firestore profile save failed:', err);
      });
    }

    setSaveStatus('saved');
    toast.success('Profile saved', 'Your public profile has been updated successfully.');
    setTimeout(() => setSaveStatus('idle'), 2000);
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm mb-6" aria-label="Breadcrumb">
          <Link to="/dashboard/practitioner" className="text-purple-600 hover:text-purple-700 font-medium transition-colors">
            Dashboard
          </Link>
          <span className="text-gray-400">/</span>
          <Link to="/dashboard/settings" className="text-purple-600 hover:text-purple-700 font-medium transition-colors">
            Settings
          </Link>
          <span className="text-gray-400">/</span>
          <span className="text-gray-900 font-medium">Public Profile</span>
        </nav>

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Public Profile</h1>
          <p className="text-gray-600">Manage how you appear to clients on your profile and website</p>
        </div>

        {/* Content */}
        <div className="space-y-6">
          {/* Section 1: Profile Photo */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Camera className="w-5 h-5 text-purple-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">Profile Photo</h3>
                <p className="text-sm text-gray-600 mb-4">
                  This photo appears on your profile card and public page.
                </p>

                <PhotoUpload
                  currentPhoto={profilePhoto}
                  showIntro={false}
                  onPhotoChange={async (url, path) => {
                    setProfilePhoto(url);
                    setProfilePhotoPath(path);

                    // Preserve B34b post-upload persistence chain
                    const existing = JSON.parse(localStorage.getItem('practitioner_setup') || '{}');
                    const updatedProfile = { ...(existing.profile || {}), photo: url, photoPath: path };
                    localStorage.setItem('practitioner_setup', JSON.stringify({ ...existing, profile: updatedProfile }));

                    const userId = firestoreService.getCurrentUserId();
                    if (userId) {
                      firestoreService.saveProfile(userId, updatedProfile).catch(err =>
                        console.warn('⚠️  Profile photo Firestore save failed:', err)
                      );
                      firestoreService.updateWebsiteDraftField(userId, 'profile.photo', url).catch(err =>
                        console.warn('⚠️  website_draft.profile.photo sync failed:', err)
                      );
                      firestoreService.updateWebsiteDraftField(userId, 'profile.photoPath', path).catch(err =>
                        console.warn('⚠️  website_draft.profile.photoPath sync failed:', err)
                      );
                      try {
                        const draftLS = JSON.parse(localStorage.getItem('practitioner_website_draft') || '{}');
                        draftLS.profile = { ...(draftLS.profile || {}), photo: url, photoPath: path };
                        localStorage.setItem('practitioner_website_draft', JSON.stringify(draftLS));
                      } catch (_) { /* non-fatal */ }
                    }
                  }}
                />

                <p className="text-xs text-gray-500 mt-2">
                  JPG, PNG, GIF, or WebP, max 5MB. Square photos work best.
                </p>
              </div>
            </div>
          </div>

          {/* Section 2: Display Name */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <User className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">Display Name</h3>
                <p className="text-sm text-gray-600 mb-4">
                  This is how clients will see your name.
                </p>

                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Your professional name"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Section 3: Modalities */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-purple-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 mb-1">Modalities</h3>
                <p className="text-sm text-gray-600 mb-4">
                  The practices and services you offer. These appear on your directory profile and help seekers find you.
                </p>
                <ModalitySelector
                  selectedModalities={modalities.selected}
                  customModalities={modalities.custom}
                  onChange={(selected, custom) =>
                    setModalities((prev) => ({ ...prev, selected, custom }))
                  }
                />
              </div>
            </div>
          </div>

          {/* Section 4: Bio (Directory) with AI Help — renumbered; was Section 3 */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <User className="w-5 h-5 text-purple-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">Bio (Directory)</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Short introduction that appears on your profile card in search results. 75-125 words.
                </p>

                <textarea
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  placeholder="Write a compelling introduction..."
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                />

                {/* AI Link & Word Counter */}
                <div className="flex items-center justify-between mt-2">
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => setShowBioModal(true)}
                    onKeyDown={(e) => { if (e.key === 'Enter') setShowBioModal(true); }}
                    className="flex items-center gap-1.5 text-sm font-medium cursor-pointer select-none"
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#6D28D9'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = '#7c3aed'; }}
                    style={{ color: '#7c3aed', transition: 'color 0.15s ease' }}
                  >
                    <Sparkles className="w-4 h-4" />
                    Need help? Let AI write this for you
                  </span>

                  <span className={`text-sm font-medium ${
                    bioWordCount > 125
                      ? 'text-red-600'
                      : bioWordCount >= 115
                        ? 'text-amber-600'
                        : 'text-gray-600'
                  }`}>
                    {bioWordCount} / 125 words
                    {bioWordCount > 125 && (
                      <span className="ml-1">
                        ({bioWordCount - 125} over limit)
                      </span>
                    )}
                  </span>
                </div>

                {bioWordCount > 125 && (
                  <p className="text-sm text-red-600 mt-2">
                    Your bio is too long. Please shorten it to 125 words or less.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Section 4: About (Website) with AI Help */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <User className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">About You (Website)</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Your full story that appears on your website. Share your journey, philosophy, and what makes you unique. 200-500 words.
                </p>

                <textarea
                  value={about}
                  onChange={(e) => setAbout(e.target.value)}
                  placeholder="Tell your story..."
                  rows={8}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                />

                {/* AI Link & Word Counter */}
                <div className="flex items-center justify-between mt-2">
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => setShowAboutModal(true)}
                    onKeyDown={(e) => { if (e.key === 'Enter') setShowAboutModal(true); }}
                    className="flex items-center gap-1.5 text-sm font-medium cursor-pointer select-none"
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#6D28D9'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = '#7c3aed'; }}
                    style={{ color: '#7c3aed', transition: 'color 0.15s ease' }}
                  >
                    <Sparkles className="w-4 h-4" />
                    Need help? Let AI write this for you
                  </span>

                  <span className={`text-sm font-medium ${
                    aboutWordCount > 500
                      ? 'text-red-600'
                      : aboutWordCount >= 480
                        ? 'text-amber-600'
                        : 'text-gray-600'
                  }`}>
                    {aboutWordCount} / 500 words
                    {aboutWordCount > 500 && (
                      <span className="ml-1">
                        ({aboutWordCount - 500} over limit)
                      </span>
                    )}
                  </span>
                </div>

                {aboutWordCount > 500 && (
                  <p className="text-sm text-red-600 mt-2">
                    Your about section is too long. Please shorten it to 500 words or less.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Section 5: Business Contact (Optional) */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Mail className="w-5 h-5 text-purple-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">Business Contact (Optional)</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Your website includes a booking button by default. Add email or phone ONLY if you want clients to reach you outside the platform.
                </p>

                <div className="space-y-4">
                  {/* Business Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Business Email
                    </label>
                    <input
                      type="email"
                      value={businessEmail}
                      onChange={(e) => handleEmailChange(e.target.value)}
                      placeholder="your@email.com"
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                        emailError ? 'border-red-400' : 'border-gray-300'
                      }`}
                    />
                    {emailError && (
                      <p className="text-red-500 text-xs mt-1">{emailError}</p>
                    )}
                  </div>

                  {/* Business Phone */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Business Phone
                    </label>
                    <input
                      type="tel"
                      value={businessPhone}
                      onChange={(e) => setBusinessPhone(formatPhoneNumber(e.target.value))}
                      onBlur={handlePhoneBlur}
                      placeholder="(555) 123-4567"
                      maxLength={14}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  {/* Privacy Toggles - Always visible */}
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-sm font-medium text-gray-700 mb-3">Privacy Settings</p>
                    <div className="space-y-3">
                      <label className={`flex items-center gap-3 ${!businessEmail ? 'opacity-50' : 'cursor-pointer'}`}>
                        <input
                          type="checkbox"
                          checked={showEmailPublicly}
                          onChange={(e) => setShowEmailPublicly(e.target.checked)}
                          disabled={!businessEmail}
                          className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                        <span className="text-sm text-gray-700">Show email on website</span>
                      </label>
                      <label className={`flex items-center gap-3 ${!businessPhone ? 'opacity-50' : 'cursor-pointer'}`}>
                        <input
                          type="checkbox"
                          checked={showPhonePublicly}
                          onChange={(e) => setShowPhonePublicly(e.target.checked)}
                          disabled={!businessPhone}
                          className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                        <span className="text-sm text-gray-700">Show phone on website</span>
                      </label>
                    </div>
                    <p className="text-sm text-gray-600 mt-3 pt-3 border-t border-gray-200">
                      💡 Tip: Most practitioners keep bookings as the primary contact method and use email/phone for business inquiries only.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 7: Social Media (read-only status) */}
          <div id="social-accounts" className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Share2 className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">Social Media</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Connection status for your verified social accounts.
                </p>
                <SocialMediaConnections />
              </div>
            </div>
          </div>

          {/* Section 8: Website */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Globe className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">Website</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Your personal or business website.
                </p>
                <WebsiteUrlInput
                  websiteUrl={websiteUrl}
                  onWebsiteChange={setWebsiteUrl}
                  websiteVerified={websiteVerified}
                  onWebsiteVerify={handleVerifyWebsite}
                />
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              onClick={handleSaveProfile}
              disabled={saveStatus === 'saving' || bioWordCount > 125 || aboutWordCount > 500 || emailError}
              className="px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? '✓ Saved' : 'Save Profile'}
            </button>
          </div>
        </div>
      </div>

      {/* AI Generator Modals */}
      <BioGeneratorModal
        isOpen={showBioModal}
        onClose={() => setShowBioModal(false)}
        onBioGenerated={handleBioGenerated}
      />

      <AboutGeneratorModal
        isOpen={showAboutModal}
        onClose={() => setShowAboutModal(false)}
        onAboutGenerated={handleAboutGenerated}
      />
    </div>
  );
};

export default PublicProfile;
